import type { DiffViewMode } from '../changes/DiffView';

/*
 * Per-branch Review tab state in localStorage. The tab unmounts on every tab switch, so anything
 * worth keeping has to outlive the component — and reloads. Every write swallows storage failures
 * (private mode, quota): the setting still applies for this session, it just is not remembered.
 */

function branchKey(prefix: string, base: string) {
  return `${prefix}.${base.split('/').pop() ?? base}`;
}

function writeItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage unavailable — the in-memory state still applies for this session
  }
}

/** Per-session viewed check-offs. */
const viewedKey = (base: string) => branchKey('zooid.changes.viewed', base);

export function readViewed(base: string): string[] {
  try {
    const raw = localStorage.getItem(viewedKey(base));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : [];
  } catch {
    return [];
  }
}

export function writeViewed(base: string, viewed: string[]) {
  writeItem(viewedKey(base), JSON.stringify(viewed));
}

export interface DiffPrefs {
  view: DiffViewMode;
  hideWhitespace: boolean;
}

const DEFAULT_DIFF_PREFS: DiffPrefs = { view: 'unified', hideWhitespace: false };

const diffPrefsKey = (base: string) => branchKey('zooid.changes.diffprefs', base);

export function readDiffPrefs(base: string): DiffPrefs {
  try {
    const raw = localStorage.getItem(diffPrefsKey(base));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') {
      const p = parsed as Partial<DiffPrefs>;
      return {
        view: p.view === 'split' ? 'split' : 'unified',
        hideWhitespace: p.hideWhitespace === true,
      };
    }
  } catch {
    // fall through to defaults
  }
  return DEFAULT_DIFF_PREFS;
}

export function writeDiffPrefs(base: string, prefs: DiffPrefs) {
  writeItem(diffPrefsKey(base), JSON.stringify(prefs));
}

/** Whether a review has been started for this branch. Persisted so the tab does not re-gate (and
 * re-charge for a draft) once the user is past it. */
const reviewStartedKey = (base: string) => branchKey('zooid.review.started', base);

export function readReviewStarted(base: string): boolean {
  try {
    return localStorage.getItem(reviewStartedKey(base)) === '1';
  } catch {
    return false;
  }
}

export function writeReviewStarted(base: string) {
  writeItem(reviewStartedKey(base), '1');
}
